import React, { useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  HeartIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { toast, Toaster } from "react-hot-toast";

// -----------------------------
// NAVBAR
// -----------------------------
const navItems = [
  { label: "Főoldal", to: "/" },
  { label: "Az együttesről", to: "/about" },
  { label: "Éves beszámolók", to: "/reports" },
  { label: "Elnyert pályázatok", to: "/grants" },
  // { label: "Hírek", to: "/news" },
  { label: "Civilek a Balaton-ért", to: "/support" },
  { label: "Támogatóink", to: "/supporters" },
  { label: "Galéria", to: "/gallery" },
  { label: "Kapcsolat", to: "/contact" }
];

const infoLinks = [
  { label: "Főoldal",        to: "/" },
  { label: "Az együttesről",      to: "/about" },
  // { label: "Hírek / Események",   to: "/news" },
  { label: "Támogatóink",         to: "/supporters" },
  { label: "Galéria",             to: "/gallery" },
  { label: "Kapcsolat",           to: "/contact" },
  { label: "Adatkezelési tájékoztató", to: "/privacy-policy" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobile = () => setMobileOpen((p) => !p);
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur shadow-sm">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="./images/BTE_LOGO.png" alt="Balaton TE" className="h-16 sm:h-20 w-auto" />
          <span className="sr-only">Balaton Táncegyüttes</span>
        </Link>

        {/* Desktop menu */}
        <ul className="hidden lg:flex items-center gap-6 text-sm font-medium">
          {navItems.map((item) => (
            <li key={item.label} className="relative">
              {item.dropdown ? (
                <>
                  {/* Dropdown trigger */}
                  <button
                    onClick={() =>
                      setOpenDropdown((d) => (d === item.label ? null : item.label))
                    }
                    className="flex items-center gap-1 text-gray-600 hover:text-primary transition-colors"
                  >
                    {item.label}
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  {/* Dropdown panel */}
                  {openDropdown === item.label && (
                    <motion.ul
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="absolute left-0 mt-2 min-w-[8rem] rounded-md border bg-white p-2 shadow-lg"
                    >
                      {item.dropdown.map((sub) => (
                        <li key={sub.label}>
                          <NavLink
                            to={sub.to}
                            className={({ isActive }) =>
                              `block whitespace-nowrap px-3 py-1.5 text-sm rounded-md hover:bg-gray-50 ${
                                isActive ? "text-primary" : "text-gray-600"
                              }`
                            }
                          >
                            {sub.label}
                          </NavLink>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </>
              ) : (
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 transition-colors ${
                      isActive ? "text-primary" : "text-gray-600 hover:text-primary"
                    }`
                  }
                >
                  {item.label === "Bemutatkozás" && (
                    <HeartIcon className="h-4 w-4" />
                  )}
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden inline-flex items-center justify-center p-2 text-gray-700"
          onClick={toggleMobile}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </nav>

      {/* Mobile pane */}
      {mobileOpen && (
        <motion.ul
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="space-y-1 bg-white px-4 pb-4 lg:hidden shadow-inner"
        >
          {navItems.map((item) => (
            <li key={item.label}>
              <NavLink
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-gray-700 hover:bg-gray-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
              {item.dropdown && (
                <ul className="ml-4 space-y-1 pt-1">
                  {item.dropdown.map((sub) => (
                    <li key={sub.label}>
                      <NavLink
                        to={sub.to}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `block rounded-md px-3 py-1 text-sm transition-colors ${
                            isActive
                              ? "bg-primary/10 text-primary"
                              : "text-gray-600 hover:bg-gray-50"
                          }`
                        }
                      >
                        {sub.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </motion.ul>
      )}
    </header>
  );
}

// -----------------------------
// FOOTER
// -----------------------------
export function Footer() {
  const formRef = useRef(null);
  const [sending, setSending] = useState(false);


  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);

    emailjs.init("slCDG9KliU1w0z5lI");

    try {
      await emailjs.sendForm(
        'service_vsfs2w8',
        'template_o8rhpbn',
        e.target
      );

      toast.success("Köszönjük! Üzenetét elküldtük. 😊");
      formRef.current.reset();
    } catch (err) {
      console.error(err);
      toast.error("Hoppá! Nem sikerült elküldeni. Próbáld újra.");
    } finally {
      setSending(false);
    }
  }

  return (
    <footer className="bg-neutral-900 text-gray-300 pt-12 pb-6">
      <div className="mx-auto max-w-7xl px-6 grid gap-10 md:grid-cols-4">
        {/* Információ */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-emerald-400">Információ</h3>
          <ul className="space-y-2 text-sm">
            {infoLinks.map(link => (
              <li key={link.label}>
                <NavLink
                  to={link.to}
                  className="hover:text-white"
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Írjon nekünk! */}
        <div>
        <h3 className="mb-4 text-lg font-semibold text-rose-500">
          Írjon nekünk!
        </h3>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex flex-col gap-3"
        >
          <input
            name="user_nev"
            type="text"
            placeholder="Név"
            required
            className="rounded-md bg-neutral-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
          />
          <input
            name="user_email"
            type="email"
            placeholder="E-mail"
            required
            className="rounded-md bg-neutral-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
          />
          <textarea
            name="targy"
            placeholder="Üzenet"
            rows="3"
            required
            className="rounded-md bg-neutral-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
          />
          <button
            type="submit"
            disabled={sending}
            className="self-start rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {sending ? "Küldés…" : "Küldés"}
          </button>
        </form>
      </div>

        {/* Kövessen minket! */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-sky-400">Kövessen minket!</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-5 w-5 text-sky-500"
              >
                <path d="M22 12a10 10 0 1 0-11.5 9.9v-7h-2v-2.9h2V9.8c0-2 1.2-3.1 3-3.1.9 0 1.8.2 1.8.2v2h-1c-1 0-1.3.6-1.3 1.2v1.8h2.3l-.4 2.9h-1.9v7A10 10 0 0 0 22 12Z" />
              </svg>
              <a href="https://www.facebook.com/Balaton.Tancegyuttes/" target="_blank" rel="noreferrer" className="hover:text-white">
                Facebook
              </a>
            </li>
            <li className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-5 w-5 text-red-500"
              >
                <path d="M10 15V9l5 3-5 3ZM21.8 8.001a3 3 0 0 0-2.106-2.123C17.885 5.3 12 5.3 12 5.3s-5.885 0-7.694.578A3 3 0 0 0 2.2 8.001 31.379 31.379 0 0 0 2 12a31.378 31.378 0 0 0 .2 3.999 3 3 0 0 0 2.106 2.12C5.92 18.7 12 18.7 12 18.7s5.885 0 7.694-.581A3 3 0 0 0 21.8 16A31.378 31.378 0 0 0 22 12a31.379 31.379 0 0 0-.2-3.999Z" />
              </svg>
              <a href="https://www.youtube.com/channel/UChQ4mmMQ3Kh4HVBJeuZ-hUg" target="_blank" rel="noreferrer" className="hover:text-white">
                Youtube
              </a>
            </li>
          </ul>
        </div>

        {/* Kapcsolat */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-rose-500">Kapcsolat</h3>
          <address className="space-y-3 not-italic text-sm">
            <p className="flex items-start gap-2">
              <MapPinIcon className="h-5 w-5 text-secondary" />
              8600 Siófok, Szépvölgyi u. 2.
            </p>
            <p className="flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5 text-secondary" />
              <a href="mailto:info@balatonte.hu" className="hover:text-white">
                info@balatonte.hu
              </a>
            </p>
            <p className="flex items-center gap-2">
              <PhoneIcon className="h-5 w-5 text-secondary" />
              19192716-1-14
            </p>
          </address>
        </div>
      </div>

      <div className="mt-10 border-t border-neutral-700 pt-4 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} Balaton Tánc Egyesület. Minden jog fenntartva.
      </div>

      <Toaster
        position="bottom-center"
        toastOptions={{
          style: { background: "#1f2937", color: "#fff" },
        }}
      />
    </footer>
  );
}